import type { FC } from "react";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { isMobile } from "react-device-detect";
import { useDispatch } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { useAuth0 } from "@auth0/auth0-react";

import { AdminRoutes } from "./router/PrivateRoutes";
import { PlaylistSRProvider } from "./providers/PlaylistSRProvider";
import { UserService } from "./api";
import { AdminPage } from "./pages/Admin";
import { LoginPage } from "./pages/Login";
import { TvModeV2 } from "./pages/TvModeV2";
import { VotePage } from "./pages/Vote";
import { PlaylistCallbacksProvider } from "./providers/EventsProvider";
import { login, logout } from "./redux/slices/auth";
import { getFromLocalStorage } from "./utils/localStorage";
import * as Str from "./utils/reader/string";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { DataDeletion } from "./pages/DataDeletion";
import { TermsOfUse } from "./pages/TermsOfUse";
import { GlobalChat } from "./pages/Chat/GlobalChat";
import { PrivateChat } from "./pages/Chat/PrivateChat";
import { Chats } from "./pages/Chats";
import { HeaderTabs } from "./components/HeaderTabs";
import { ChatsSRProvider } from "./providers/ChatsSRProvider";
import { useMutation } from "react-query";

enum LoadingState {
  loading = "loading",
  loaded = "loaded"
}

export const App: FC = () => {
  const dispatch = useDispatch();
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.loading
  );
  const user = useAuth0();
  const location = useLocation();

  const addAuth0UserMutation = useMutation(["addAuth0User"], {
    mutationFn: async (token: string) => {
      return UserService.auth0Create({
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    }
  });

  useEffect(() => {
    if (user.isAuthenticated) {
      user
        .getAccessTokenSilently()
        .then((token) => {
          dispatch(
            login({
              value: token,
              currentUser: {
                fullName: user.user?.name ?? "",
                auth0Id: user.user?.sub ?? "",
                userName: user.user?.nickname ?? "",
                firstName: user.user?.given_name ?? "",
                lastName: user.user?.family_name ?? ""
              }
            })
          );

          addAuth0UserMutation.mutate(token);
        })
        .catch(() => {
          if (user.isAuthenticated) {
            user.logout();
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dispatch]);

  useEffect(() => {
    const userJwt = Str.read(getFromLocalStorage("userJwt"));

    if (!userJwt || userJwt.length === 0) {
      setLoadingState(LoadingState.loaded);
      return;
    }

    UserService.currentList({
      headers: {
        Authorization: `Bearer ${userJwt}`
      }
    })
      .then((res) => {
        if (res.status === 200) {
          dispatch(login({ value: userJwt, currentUser: res.data }));
        } else {
          dispatch(logout());
        }
      })
      .catch(() => {
        dispatch(logout());
      })
      .finally(() => {
        setLoadingState(LoadingState.loaded);
      });
  }, [dispatch]);

  return (
    <>
      {loadingState === LoadingState.loading || user.isLoading ? (
        <div className="center-loading-block">
          <CircularProgress />
        </div>
      ) : (
        <>
          {location.pathname !== "/login" &&
            location.pathname !== "/player" && <HeaderTabs />}

          <Routes>
            <Route path="/login" element={<LoginPage />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/data-deletion" element={<DataDeletion />} />

            <Route element={<ChatsSRProvider />}>
              <Route path="chat">
                <Route path="global" element={<GlobalChat />} />
                <Route path=":interlocutorId" element={<PrivateChat />} />
                <Route path="*" index element={<Chats />} />
              </Route>

              <Route element={<PlaylistCallbacksProvider />}>
                <Route element={<PlaylistSRProvider />}>
                  <Route path="/vote" element={<VotePage />} />

                  <Route element={<AdminRoutes />}>
                    <Route path="/admin/*" element={<AdminPage />} />

                    <Route
                      path="/player"
                      element={isMobile ? <Navigate to="/" /> : <TvModeV2 />}
                    />
                  </Route>
                </Route>

                <Route path="*" index element={<Navigate to="/vote" />} />
              </Route>
            </Route>
          </Routes>
        </>
      )}
    </>
  );
};
